<template>
  <q-dialog ref="dialogRef" persistent transition-hide="flip-up" transition-show="flip-down" @hide="onHide">
    <q-card class="q-dialog-plugin" style="color:#5d5d5d;">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">Login / Sign-up</div>
        <q-space/>
        <q-btn v-close-popup dense flat icon="fa-solid fa-xmark" round/>
      </q-card-section>
      <Transition>
        <q-card-section v-if="!emailSent" class="q-px-md">
          <h6 class="text-h6 text-left q-my-md">
              Enter your email below. We’ll send you a one-time password to quickly access your account or create one if you’re new.
          </h6>

          <q-form class="text-center" @submit="onSubmit">
            <q-input v-model="email"
                     :rules="emailValidationRules"
                     dense
                     outlined
                     type="email"
            />
            <p class="text-left">
              <q-checkbox
                  v-model="tcAccepted"
                  color="secondary"
                  :true-value="true"
                  :false-value="false" />
              I agree to the <a href="#"
                                @click.prevent="eventBus.emit('showTermsAndConditions')"
                                title="Terms and Conditions">Terms and Conditions</a> and
              <a href="#"
                 @click.prevent="eventBus.emit('showPrivatePolicy')"
                 title="Privacy Policy">Privacy Policy</a>
            </p>

            <q-btn class="text-center q-my-sm"
                   color="primary"
                   label="Send one-time password"
                   no-caps
                   type="submit"
                   :disable="!tcAccepted"
            />
          </q-form>
            <div class="q-my-sm">
                <p>As a Registered User you can create for FREE unlimited Memo.Links:</p>
                <p class="text-blue text-subtitle2">
                    https://<span class="text-deep-orange">Your-Domain</span>.{{ appDomain }}/
                    <span class="text-deep-orange">Your-Keyword</span>
                </p>
                <p>With your custom domain name and your keywords</p>
            </div>
        </q-card-section>

        <q-card-section class="q-px-md" v-else>
          <h6 class="text-h6 text-center">Check your inbox at <span class="text-primary">{{ email }}</span> !</h6>
          <p class="text-h5 text-center">You will receive a one-time-password to access</p>
          <p class="text-h5 text-center">your NEW or EXISTING account</p>
          <q-form class="text-center" @submit="onWordsSubmit">
            <q-input v-model="words"
                     :rules="[
                     val => !!val && val.length > 0 || 'Please enter the 3 keywords from the email',
                 ]"
                     dense
                     outlined
                     type="text"
            />
            <q-btn class="text-center q-mt-sm" color="primary" label="Let me in!" no-caps type="submit"/>
          </q-form>

        </q-card-section>

      </Transition>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { useDialogPluginComponent, useQuasar } from 'quasar'
import { ref, inject } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { event } from 'vue-gtag'

const quasar = useQuasar();
const store = useStore();
const router = useRouter();
const eventBus = inject('eventBus');
const axios = inject('axios');
const appDomain = inject('appDomain');

const email = ref('');
const emailSent = ref(false);
const words = ref('');
const tcAccepted = ref(false);

const emailValidationRules = ref([
    val => !!val && val.length > 0 || 'Please enter your email',
    val => /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(val) || 'Enter a valid email',
]);

async function onSubmit() {
  quasar.loading.show();

  await axios.post('auth/send-magic-link', {
    email: email.value.trim(),
    memoLinkId: props.memoLinkId,
    acceptPolicies: tcAccepted.value
  })
      .then((response) => {
        if (response?.name === 'AxiosError') {
          quasar.notify({
            progress: true,
            message: response.response.data.errors[0],
            type: 'negative',
            position: 'top',
            multiLine: true
          });
        } else if (response.status === 202) {
          emailSent.value = true;
          event('send-magic-link', {method: 'Google'});
        }
      })
      .catch(() => {
        quasar.notify({
          progress: true,
          message: 'An error occurred',
          type: 'negative',
          position: 'top',
          multiLine: true
        });
      })
      .finally(() => {
        quasar.loading.hide()
      });
}

async function onWordsSubmit() {
  quasar.loading.show();

  axios
      .put('auth/login', {
        email: email.value.trim(),
        words: words.value.trim()
      })
      .then((response) => {
        if (response?.name === 'AxiosError') {
          quasar.notify({
            progress: true,
            message: response.response.data.errors ? response.response.data.errors[0] : 'General error occurred',
            type: 'negative',
            position: 'top',
            multiLine: true
          });
        } else {

          const data = response.data.data;

          quasar.cookies.set('_auth_token', data.token.value, {
            expires: new Date(data.token.expires_at * 1000),
            path: '/',
            sameSite: 'Strict',
          });

          axios.defaults.headers.common['Authorization'] = `Bearer ${data.token.value}`;
          store.commit('setToken', data.token.value);
          store.commit('auth', data.user)
            store.dispatch('fetchLimits')

          dialogRef.value.hide();
          event('login', {method: 'Google'});
          router.push({name: 'home'});
        }
      })
      .finally(() => quasar.loading.hide());
}

defineEmits([
  // REQUIRED; need to specify some events that your
  // component will emit through useDialogPluginComponent()
  ...useDialogPluginComponent.emits
]);

const props = defineProps(['memoLinkId']);

const {dialogRef, onDialogHide} = useDialogPluginComponent()

function onHide() {
  emailSent.value = false;
  onDialogHide();
}
</script>

<style lang="sass" scoped>
body.screen--xl, body.screen--lg
  .q-dialog-plugin
    width: 800px

</style>
